import React, { useCallback, useMemo, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import Slide from '@mui/material/Slide';
import styled from 'styled-components';
import { convertToY, isYMap, isYArray, slateToString } from '../../hooks/documentHooks';
import { usePreview } from '../PreviewContext';
import { useDispatch, useSelector } from 'react-redux';
import LAXSON from 'laxson';
import { extractTranslation, mergeTranslation } from '../../helpers/GetDataJson';
import CodeEditor from '../CodeEditor';

const BodyContainer = styled.div`
  padding: 20px;
  height: calc(100vh - 64px);
  overflow: auto;
`

const appBarStyle = { position: 'relative' }
const titleStyle = { ml: 2, flex: 1 }

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const JsonEditor = () => {
  const data = useSelector(state => state.editJsonData)
  const dataTranslation = useSelector(state => state.editJsonDataTranslation)
  const isOpen = !!data
  const initialValue = useMemo(() => {
    const final = dataTranslation ? mergeTranslation({ data, dataTranslation }) : (data && data.toJSON())
    return JSON.stringify(final, null, 2)
  }, [dataTranslation, data])
  const [value, setValue] = useState(initialValue)
  const { hotReloadPage } = usePreview()
  const dispatch = useDispatch()

  const onClose = useCallback(() => {
    dispatch({
      type: 'edit-json',
      data: null
    })
  }, [dispatch])

  const handleSave = useCallback(() => {
    const objectValue = LAXSON.parse(slateToString(value))
    if (dataTranslation) {
      const result = extractTranslation({ data, translatedJson: objectValue })
      for (let key of dataTranslation.keys()) {
        dataTranslation.delete(key)
      }
      Object.entries(result).forEach(([key, value]) => {
        dataTranslation.set(key, value);
      })  
    } else {
      if (isYMap(data)) {
        for (let key of data.keys()) {
          data.delete(key)
        }
        Object.entries(objectValue).forEach(([key, value]) => {
          data.set(key, convertToY(value));
        })
      } else if (isYArray(data)) {
        data.delete(0, data.length)
        objectValue.forEach(v => {
          const yData = convertToY(v)
          data.push([yData])
        })
      }  
    }
    hotReloadPage()
    onClose()
  }, [data, dataTranslation, hotReloadPage, onClose, value])

  return (
    <>
      <Dialog
        fullScreen
        open={isOpen}
        onClose={onClose}
        TransitionComponent={Transition}
      >
        <AppBar sx={appBarStyle}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={onClose}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
            <Typography sx={titleStyle} variant="h6" component="div">
              JSON Editor
            </Typography>
            <Button autoFocus color="inherit" onClick={handleSave}>
              Save
            </Button>
          </Toolbar>
        </AppBar>
        <BodyContainer>
          <CodeEditor initialValue={initialValue} language='json' onChange={setValue} />
        </BodyContainer>
      </Dialog>
    </>
  )
}

export default JsonEditor
